import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Chip,
  Stack,
  Tooltip,
  Alert,
  Autocomplete,
} from "@mui/material";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaceIcon from "@mui/icons-material/Place";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Chart } from "react-google-charts";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_URL = localStorage.getItem("apiUrl");
const token = localStorage.getItem("token");

const lineOptions = {
  legend: { position: "none" },
  backgroundColor: "transparent",
  chartArea: {
    left: 35,
    right: 15,
    top: 20,
    bottom: 35,
    width: "100%",
    height: "75%",
  },
  hAxis: {
    textStyle: { color: "#6b7280", fontSize: 12 },
    baselineColor: "#e5e7eb",
    gridlines: { color: "transparent" },
  },
  vAxis: {
    minValue: 0,
    format: "0",
    textStyle: { color: "#6b7280", fontSize: 12 },
    baselineColor: "#e5e7eb",
    gridlines: { color: "#eef2f7" },
  },
  colors: ["#A7B85A"],
  lineWidth: 3,
  pointSize: 5,
  areaOpacity: 0.12,
};

const toApiDate = (d: Date) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

const getRange = (period: string): { ini: Date; fim: Date } => {
  const d = new Date();
  const hoje = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (period === "hoje") return { ini: hoje, fim: hoje };
  if (period === "semana") {
    const ini = new Date(hoje);
    ini.setDate(hoje.getDate() - hoje.getDay());
    return { ini, fim: hoje };
  }
  if (period === "30dias") {
    const ini = new Date(hoje);
    ini.setDate(hoje.getDate() - 29);
    return { ini, fim: hoje };
  }
  return { ini: new Date(hoje.getFullYear(), hoje.getMonth(), 1), fim: hoje };
};

const getPrevRange = (ini: Date, fim: Date): { ini: Date; fim: Date } => {
  // Se o período começa no dia 1 (como "Este Mês"), comparamos com os mesmos dias do mês anterior (Ex: 01 a 14 de Março vs 01 a 14 de Fevereiro)
  if (ini.getDate() === 1) {
    const prevIni = new Date(ini.getFullYear(), ini.getMonth() - 1, 1);
    const lastDayOfPrevMonth = new Date(
      ini.getFullYear(),
      ini.getMonth(),
      0,
    ).getDate();
    const dayFim = Math.min(fim.getDate(), lastDayOfPrevMonth);
    const prevFim = new Date(ini.getFullYear(), ini.getMonth() - 1, dayFim);
    return { ini: prevIni, fim: prevFim };
  }

  // Para outros casos (Hoje, Semana, 30 dias ou Custom sem ser início de mês), espelhamos a duração
  const diff = fim.getTime() - ini.getTime();
  const prevFim = new Date(ini.getTime() - 1);
  const prevIni = new Date(prevFim.getTime() - diff);
  return { ini: prevIni, fim: prevFim };
};

const Trend = ({
  current,
  previous,
}: {
  current: number;
  previous: number;
}) => {
  if (previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  const up = pct >= 0;
  return (
    <Box display="flex" alignItems="center" gap={0.3} mt={0.3}>
      {up ? (
        <TrendingUpIcon sx={{ fontSize: 14, color: "#4caf50" }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 14, color: "#f44336" }} />
      )}
      <Typography
        fontSize={11}
        color={up ? "#4caf50" : "#f44336"}
        fontWeight={700}
      >
        {up ? "+" : ""}
        {pct.toFixed(1).replace(".", ",")}%
      </Typography>
    </Box>
  );
};

const StatCard = ({
  icon,
  value,
  title,
  subtitle,
  bg,
  trend,
}: {
  icon: React.ReactNode;
  value: string;
  title: string;
  subtitle: string;
  bg: string;
  trend?: { current: number; previous: number };
}) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 3,
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      background: bg,
    }}
  >
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(255,255,255,0.75)",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography fontWeight={800} fontSize={20} lineHeight={1}>
          {value}
        </Typography>
        {trend && <Trend current={trend.current} previous={trend.previous} />}
        <Typography fontWeight={700} fontSize={13} mt={0.3}>
          {title}
        </Typography>
        <Typography fontSize={11} color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const InfoMiniCard = ({
  icon,
  title,
  value,
  subtitle,
  bg,
  iconColor,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  bg: string;
  iconColor: string;
}) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      background: bg,
      height: "100%",
    }}
  >
    <CardContent sx={{ py: 2.2 }}>
      <Box display="flex" alignItems="center" gap={1.2} mb={1}>
        <Box sx={{ color: iconColor, display: "flex", alignItems: "center" }}>
          {icon}
        </Box>
        <Typography fontSize={13} fontWeight={700}>
          {title}
        </Typography>
      </Box>
      <Typography fontSize={28} fontWeight={800} lineHeight={1}>
        {value}
      </Typography>
      <Typography fontSize={12} color="text.secondary" mt={0.5}>
        {subtitle}
      </Typography>
    </CardContent>
  </Card>
);

const VisitMap = ({
  points,
  mode,
}: {
  points: { lat: number; lng: number; peso: number }[];
  mode: "calor" | "pontos";
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const heatLayerRef = useRef<any>(null);
  const markersRef = useRef<L.CircleMarker[]>([]);
  const isLocatingRef = useRef(false);

  // ── Inicializa o mapa UMA vez ──────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, { zoomControl: false }).setView(
      [0, 0],
      2,
    );
    mapInstanceRef.current = map;

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    ).addTo(map);

    setTimeout(() => map.invalidateSize(), 100);

    // Botões customizados
    const zoomContainer = L.DomUtil.create("div", "");
    zoomContainer.style.cssText =
      "position:absolute;bottom:15px;right:15px;z-index:1000;display:flex;flex-direction:column;gap:6px;";

    const makeBtn = (label: string, onClick: () => void) => {
      const btn = L.DomUtil.create("div", "");
      btn.innerHTML = label;
      btn.style.cssText =
        "background:white;width:36px;height:36px;border-radius:6px;font-size:18px;font-weight:bold;" +
        "color:#444;display:flex;align-items:center;justify-content:center;" +
        "box-shadow:0 2px 8px rgba(0,0,0,0.2);cursor:pointer;user-select:none;";
      btn.onclick = (e) => {
        e.stopPropagation();
        onClick();
      };
      return btn;
    };

    zoomContainer.appendChild(
      makeBtn("⌖", () => {
        if (isLocatingRef.current) return;
        isLocatingRef.current = true;
        map.locate({ setView: false });
      }),
    );
    zoomContainer.appendChild(makeBtn("+", () => map.zoomIn()));
    zoomContainer.appendChild(makeBtn("−", () => map.zoomOut()));
    mapRef.current.appendChild(zoomContainer);

    map.on("locationfound", (e) => {
      isLocatingRef.current = false;
      map.flyTo(e.latlng, 16);
    });

    map.on("locationerror", () => {
      isLocatingRef.current = false;
      if (points.length > 0) {
        const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    });

    // Tenta centralizar nos dados direto, sem esperar geolocalização
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    } else {
      map.locate({ setView: true, maxZoom: 16 });
    }

    return () => {
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
      heatLayerRef.current = null;
      markersRef.current = [];
    };
  }, []);

  // ── Troca de camada quando mode ou points mudam ────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Limpa tudo
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];

    if (points.length === 0) return;

    if (mode === "calor") {
      import("leaflet.heat").then(() => {
        const heatData = points.map(
          (p) => [p.lat, p.lng, p.peso] as [number, number, number],
        );
        // @ts-ignore
        heatLayerRef.current = L.heatLayer(heatData, {
          radius: 30,
          blur: 20,
          maxZoom: 17,
        }).addTo(map);
      });
    } else {
      points.forEach((p) => {
        const marker = L.circleMarker([p.lat, p.lng], {
          radius: 8,
          fillColor: "#e53935",
          color: "#fff",
          weight: 2,
          opacity: 1,
          fillOpacity: 0.85,
        }).addTo(map);
        markersRef.current.push(marker);
      });
    }

    // Reajusta bounds sempre que os dados mudarem
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    map.invalidateSize();
  }, [points, mode]); // <-- ambos aqui: troca de mode não recria o mapa

  return (
    <div
      ref={mapRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
    />
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      minHeight: 100,
      color: "#7b8794",
      fontWeight: 700,
      fontSize: 14,
    }}
  >
    {message}
  </Box>
);

export const VisitsQueriesResultsPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartKey, setChartKey] = useState(0);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [prevData, setPrevData] = useState<any>(null);
  const [period, setPeriod] = useState<string>("mes");
  const [customIni, setCustomIni] = useState("");
  const [customFim, setCustomFim] = useState("");
  const [tiposFiltro, setTiposFiltro] = useState<string[]>([]);
  const [vendedoresList, setVendedoresList] = useState<any[]>([]);
  const [vendedorId, setVendedorId] = useState<string[]>([]);
  const [mapMode, setMapMode] = useState<"calor" | "pontos">("calor");

  const dataInvalida = !!customIni && !!customFim && customFim < customIni;
  const datasCustomValidas = !!customIni && !!customFim && !dataInvalida;

  const handleVendedorChange = (value: any[]) => {
    setVendedorId(value.map((v) => v.id));
  };

  const fetchDashboard = useCallback(
    async (iniDate: string, fimDate: string, isPrev = false) => {
      if (!API_URL || !token) return;
      try {
        const response = await axios.get(`${API_URL}/visitas/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            emissaoInicial: iniDate,
            emissaoFinal: fimDate,
            vendedorId:
              vendedorId.length > 0 ? vendedorId.join(",") : undefined,
          },
        });
        if (isPrev) setPrevData(response.data);
        else setDashboardData(response.data);
      } catch (error) {
        console.error("Erro ao buscar dashboard:", error);
      }
    },
    [vendedorId],
  );

  const loadData = useCallback(() => {
    let ini: Date, fim: Date;
    if (period === "custom") {
      if (!datasCustomValidas) {
        setDashboardData(null);
        setPrevData(null);
        return;
      }
      const [iy, im, id] = customIni.split("-").map(Number);
      const [fy, fm, fd] = customFim.split("-").map(Number);
      ini = new Date(iy, im - 1, id);
      fim = new Date(fy, fm - 1, fd);
    } else {
      ({ ini, fim } = getRange(period));
    }
    const prev = getPrevRange(ini, fim);
    fetchDashboard(toApiDate(ini), toApiDate(fim));
    fetchDashboard(toApiDate(prev.ini), toApiDate(prev.fim), true);
  }, [
    period,
    customIni,
    customFim,
    fetchDashboard,
    datasCustomValidas,
    vendedorId,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() =>
      setTimeout(() => setChartKey((p) => p + 1), 50),
    );
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (period !== "custom") {
      setCustomIni("");
      setCustomFim("");
    }
    // Opcional: Limpar dados ao trocar de período para mostrar "..."
    setDashboardData(null);
    setPrevData(null);
  }, [period]);

  const getVendedores = async () => {
    try {
      const response = await axios.get(`${API_URL}/visitas/vendedores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendedoresList(response.data.vendedores);
    } catch (error) {
      console.error("Erro ao buscar vendedores:", error);
    }
  };

  useEffect(() => {
    getVendedores();
  }, []);

  const semDadosResumo =
    !dashboardData ||
    !dashboardData.resumo ||
    dashboardData.resumo.totalVisitas === 0;
  const semDadosGeral =
    dashboardData &&
    dashboardData.visitasPorDia?.length === 0 &&
    dashboardData.tiposVisita?.length === 0;
  const loading =
    !dashboardData &&
    !dataInvalida &&
    (period !== "custom" || datasCustomValidas);

  const visitsPerDayData = [
    ["Dia", "Visitas"],
    ...(dashboardData?.visitasPorDia?.map((d: any) => [d.dia, d.visitas]) ??
      []),
  ];

  const todostipos: any[] = (dashboardData?.tiposVisita ?? []).sort(
    (a: any, b: any) => {
      const order = [
        "Reunião",
        "Venda",
        "Cliente Novo",
        "Fechou",
        "Mudou de Ramo",
        "Outros",
      ];
      const idxA = order.indexOf(a.name);
      const idxB = order.indexOf(b.name);
      if (idxA === -1 && idxB === -1) return a.name.localeCompare(b.name);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    },
  );
  const tiposVisiveis =
    tiposFiltro.length > 0
      ? todostipos.filter((t: any) => tiposFiltro.includes(t.name))
      : todostipos;

  const total = tiposVisiveis.reduce((s: number, i: any) => s + i.value, 0);
  const visitTypesChartData = [
    ["Tipo", "Quantidade", { role: "annotation" }],
    ...tiposVisiveis.map((item: any) => {
      const pct =
        total > 0
          ? ((item.value / total) * 100).toFixed(1).replace(".", ",")
          : "0";
      return [item.name, item.value, `${pct}%`];
    }),
  ];

  const pieOptions = {
    pieHole: 0.58,
    legend: "none",
    backgroundColor: "transparent",
    chartArea: { left: 20, top: 20, width: "90%", height: "90%" },
    slices: tiposVisiveis.reduce(
      (acc: any, item: any, index: number) => ({
        ...acc,
        [index]: { color: item.color },
      }),
      {},
    ),
    pieSliceText: "annotation",
    tooltip: { text: "value" },
  };

  const resumo = dashboardData?.resumo;
  const prevResumo = prevData?.resumo;
  const cobertura = dashboardData?.coberturaClientes;
  const mapaCalor = dashboardData?.mapaCalor ?? [];

  const toggleTipo = (nome: string) => {
    setTiposFiltro((prev) =>
      prev.includes(nome) ? prev.filter((t) => t !== nome) : [...prev, nome],
    );
  };

  const formatarTempo = (minutos: number) => {
    if (!minutos || minutos <= 0) return "0 min";
    if (minutos < 60) return `${minutos} min`;
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  return (
    <Box
      ref={containerRef}
      sx={{ p: 2, background: "#f6f7fb", minHeight: "100vh" }}
    >
      <Grid container spacing={2} alignItems="stretch">
        {/* FILTRO DE PERÍODO */}
        <Grid item xs={12}>
          <Card
            sx={{ borderRadius: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
          >
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography
                  fontWeight={700}
                  fontSize={14}
                  color="text.secondary"
                >
                  Período:
                </Typography>

                <ToggleButtonGroup
                  value={period}
                  exclusive
                  onChange={(_, v) => {
                    if (v) setPeriod(v);
                  }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontSize: 13,
                      px: 1.5,
                    },
                  }}
                >
                  <ToggleButton value="hoje">Hoje</ToggleButton>
                  <ToggleButton value="semana">Esta Semana</ToggleButton>
                  <ToggleButton value="30dias">Últimos 30 Dias</ToggleButton>
                  <ToggleButton value="mes">Este Mês</ToggleButton>
                  <ToggleButton value="custom">Personalizado</ToggleButton>
                </ToggleButtonGroup>

                {period === "custom" && (
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="flex-start"
                    flexWrap="wrap"
                  >
                    <TextField
                      type="date"
                      size="small"
                      label="De"
                      value={customIni}
                      onChange={(e) => setCustomIni(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <TextField
                      type="date"
                      size="small"
                      label="Até"
                      value={customFim}
                      onChange={(e) => setCustomFim(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: customIni }}
                      sx={{ width: 150 }}
                    />
                  </Box>
                )}

                {/* Divider visual */}
                <Box
                  sx={{
                    width: "1px",
                    height: 32,
                    bgcolor: "#e0e0e0",
                    mx: 0.5,
                    display: { xs: "none", md: "block" },
                  }}
                />

                {/* Filtro de Vendedores */}
                <Autocomplete
                  size="small"
                  multiple
                  options={vendedoresList}
                  getOptionLabel={(option) => option.nome}
                  filterSelectedOptions
                  value={vendedoresList.filter((s) =>
                    (vendedorId || []).includes(s.id),
                  )}
                  onChange={(_, value) => handleVendedorChange(value)}
                  noOptionsText="Sem opções"
                  sx={{ minWidth: 220 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendedores"
                      placeholder={vendedorId.length === 0 ? "Todos" : ""}
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {dataInvalida ? (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 4, py: 8 }}>
              <EmptyState message="Intervalo de datas inválido" />
            </Card>
          </Grid>
        ) : semDadosGeral && !loading ? (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 4, py: 10 }}>
              <EmptyState message="Nenhuma informação disponível para este período" />
            </Card>
          </Grid>
        ) : (
          <>
            {/* RESUMO */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{ mb: 2, color: "#263238" }}
                  >
                    Visitas Comerciais
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <StatCard
                        icon={
                          <DirectionsWalkIcon
                            sx={{ color: "#5E9C45", fontSize: 26 }}
                          />
                        }
                        value={resumo ? String(resumo.totalVisitas) : "..."}
                        title="Visitas"
                        subtitle="no período"
                        bg="#F7F1CF"
                        trend={
                          period === "custom" && resumo && prevResumo
                            ? {
                                current: resumo.totalVisitas,
                                previous: prevResumo.totalVisitas,
                              }
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StatCard
                        icon={
                          <ShoppingCartCheckoutIcon
                            sx={{ color: "#D4A100", fontSize: 26 }}
                          />
                        }
                        value={resumo ? String(resumo.vendasOriginadas) : "..."}
                        title="Vendas Originadas"
                        subtitle="das visitas"
                        bg="#FBF3D6"
                        trend={
                          period === "custom" && resumo && prevResumo
                            ? {
                                current: resumo.vendasOriginadas,
                                previous: prevResumo.vendasOriginadas,
                              }
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StatCard
                        icon={
                          <CheckCircleIcon
                            sx={{ color: "#5AA071", fontSize: 26 }}
                          />
                        }
                        value={
                          resumo
                            ? `${(resumo.conversao || 0)
                                .toFixed(1)
                                .replace(".", ",")} %`
                            : "..."
                        }
                        title="Conversão"
                        subtitle="das visitas"
                        bg="#E7F3ED"
                        trend={
                          period === "custom" && resumo && prevResumo
                            ? {
                                current: resumo.conversao,
                                previous: prevResumo.conversao,
                              }
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Visitas por dia + Mapa */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={2} height="100%">
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    flex: 1,
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <DirectionsWalkIcon sx={{ color: "#7DA453" }} />
                      <Typography fontWeight={800}>
                        Visitas por Dias da Semana
                      </Typography>
                    </Box>
                    <Box sx={{ height: 220 }}>
                      {semDadosGeral || visitsPerDayData.length <= 1 ? (
                        <EmptyState message="Sem visitas no período" />
                      ) : (
                        <Chart
                          key={`line-${chartKey}`}
                          chartType="AreaChart"
                          width="100%"
                          height="220px"
                          data={visitsPerDayData}
                          options={lineOptions}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent
                    sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <PlaceIcon sx={{ color: "#8AAE48" }} />
                        <Typography fontWeight={800}>
                          Mapa de Visitas
                        </Typography>
                      </Box>

                      {mapaCalor.length > 0 && (
                        <ToggleButtonGroup
                          value={mapMode}
                          exclusive
                          onChange={(_, v) => {
                            if (v) setMapMode(v);
                          }}
                          size="small"
                          sx={{
                            "& .MuiToggleButton-root": {
                              textTransform: "none",
                              fontSize: 11,
                              px: 1.2,
                              py: 0.3,
                            },
                          }}
                        >
                          <ToggleButton value="calor">Calor</ToggleButton>
                          <ToggleButton value="pontos">Pontos</ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 250,
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e7eaf0",
                      }}
                    >
                      {mapaCalor.length > 0 ? (
                        <VisitMap points={mapaCalor} mode={mapMode} />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: "#7b8794",
                            fontWeight: 700,
                            background:
                              "linear-gradient(180deg, #eef3e6 0%, #f6f7fb 100%)",
                          }}
                        >
                          Sem dados de localização
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            {/* Tipos de Visita */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  height: "100%",
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <DonutLargeIcon sx={{ color: "#E5B700" }} />
                    <Typography fontWeight={800}>Tipos de Visita</Typography>
                  </Box>

                  <Stack direction="row" flexWrap="wrap" gap={0.8} mb={1}>
                    {todostipos.map((item: any) => {
                      const ativo =
                        tiposFiltro.length === 0 ||
                        tiposFiltro.includes(item.name);
                      return (
                        <Tooltip
                          key={item.name}
                          title={
                            ativo
                              ? "Clique para ocultar"
                              : "Clique para mostrar"
                          }
                        >
                          <Chip
                            label={`${item.name} (${item.value})`}
                            size="small"
                            onClick={() => toggleTipo(item.name)}
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              bgcolor: ativo ? item.color : "#e0e0e0",
                              color: ativo ? "#fff" : "#999",
                              opacity: ativo ? 1 : 0.6,
                              transition: "all 0.2s",
                              "&:hover": { opacity: 0.85 },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                    {tiposFiltro.length > 0 && (
                      <Chip
                        label="Limpar filtro"
                        size="small"
                        onClick={() => setTiposFiltro([])}
                        sx={{
                          fontSize: 11,
                          cursor: "pointer",
                          bgcolor: "#f5f5f5",
                        }}
                      />
                    )}
                  </Stack>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 420,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {tiposVisiveis.length > 0 ? (
                        <Chart
                          key={`pie-${chartKey}-${tiposFiltro.join()}`}
                          chartType="PieChart"
                          width="100%"
                          height="320px"
                          data={visitTypesChartData}
                          options={pieOptions}
                        />
                      ) : (
                        <EmptyState message="Sem dados no período" />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  height: "100%",
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PeopleIcon sx={{ color: "#F0B400" }} />
                    <Typography fontWeight={800}>
                      Cobertura de Clientes
                    </Typography>
                  </Box>
                  <Grid
                    container
                    spacing={1.5}
                    sx={{ flex: 1, alignContent: "space-between" }}
                  >
                    {/* CARD: Clientes Ativos — verde */}
                    <Grid item xs={12}>
                      <InfoMiniCard
                        icon={<PeopleIcon />}
                        iconColor="#4E9A61"
                        title="Clientes Ativos"
                        value={
                          cobertura ? String(cobertura.clientesAtivos) : "..."
                        }
                        subtitle="Total de clientes ativos na carteira"
                        bg="#EDF8EF"
                      />
                    </Grid>

                    {/* CARD: Clientes Visitados — verde */}
                    <Grid item xs={12}>
                      <InfoMiniCard
                        icon={<PeopleIcon />}
                        iconColor="#4E9A61"
                        title="Clientes Visitados"
                        value={
                          cobertura
                            ? String(cobertura.clientesVisitados)
                            : "..."
                        }
                        subtitle={
                          cobertura
                            ? `${String(cobertura.porcentagemVisitados).replace(
                                ".",
                                ",",
                              )}% dos clientes ativos`
                            : "Clientes visitados no período"
                        }
                        bg="#EDF8EF"
                      />
                    </Grid>

                    {/* CARD: Frequência Média — azul */}
                    <Grid item xs={12}>
                      <InfoMiniCard
                        icon={<AccessTimeIcon />}
                        iconColor="#1976d2"
                        title="Frequência Média"
                        value={
                          cobertura
                            ? String(cobertura.frequenciaMedia).replace(
                                ".",
                                ",",
                              )
                            : "..."
                        }
                        subtitle="Média de visitas por cliente"
                        bg="#E3F2FD"
                      />
                    </Grid>

                    {/* CARD: Tempo Médio — azul */}
                    <Grid item xs={12}>
                      <InfoMiniCard
                        icon={<AccessTimeIcon />}
                        iconColor="#1976d2"
                        title="Tempo Médio de Visita"
                        value={
                          cobertura ? cobertura.tempoMedioFormatado : "..."
                        }
                        subtitle="Duração média por visita"
                        bg="#E3F2FD"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};
